import type { ReactElement } from "react";
import {
  curveCardinal,
  curveCatmullRom,
  curveLinear,
  curveMonotoneX,
  curveNatural,
  curveStep,
  curveStepAfter,
  curveStepBefore,
  line as d3Line,
} from "d3-shape";
import type { LineLayerScales } from "./layerTypes";

type LayerComponent = (scales: LineLayerScales) => ReactElement | null;
type LayerComponentWithName = LayerComponent & { displayName?: string };

export const buildPreviousPeriodLayer = ({
  prevPoints,
  seriesKeys,
  seriesColorById,
  curveName,
}: {
  prevPoints: Array<{ x: string | number; y: number }> | null;
  seriesKeys: string[];
  seriesColorById: Record<string, string>;
  curveName?: string;
}): LayerComponentWithName => {
  const color =
    seriesKeys.length === 1 ? seriesColorById[seriesKeys[0]] : undefined;
  const hasOverlay = Boolean(prevPoints && seriesKeys.length === 1 && color);

  const curveMap: Record<string, any> = {
    linear: curveLinear,
    cardinal: curveCardinal,
    catmullRom: curveCatmullRom,
    monotoneX: curveMonotoneX,
    natural: curveNatural,
    step: curveStep,
    stepAfter: curveStepAfter,
    stepBefore: curveStepBefore,
  };
  const selectedCurve = (curveName && curveMap[curveName]) || curveMonotoneX;

  const PreviousPeriodLayer: LayerComponentWithName = ({
    xScale,
    yScale,
  }: LineLayerScales): ReactElement | null => {
    if (!hasOverlay) return null;

    const coordinates = (prevPoints as Array<{ x: string | number; y: number }>)
      .map(p => {
        const xv = xScale(p.x);
        const yv = yScale(p.y);
        if (xv == null || yv == null) return null;
        return { x: Number(xv), y: Number(yv) };
      })
      .filter(Boolean) as Array<{ x: number; y: number }>;

    if (coordinates.length < 2) return null;

    const generator = d3Line<{ x: number; y: number }>()
      .x(d => d.x)
      .y(d => d.y)
      .curve(selectedCurve);

    const d = generator(coordinates) || "";

    if (!d) return null;

    return (
      <path
        d={d}
        stroke={color as string}
        strokeWidth={2}
        strokeDasharray="4 4"
        fill="none"
        opacity={0.8}
      />
    );
  };

  PreviousPeriodLayer.displayName = "PreviousPeriodLayer";
  return PreviousPeriodLayer;
};
